import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { Link, useHistory } from 'react-router-dom'
import {
  Button,
  ButtonBase,
  Card,
  Checkbox,
  Collapse,
  Icon,
  IconButton,
  InputBase,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core'
import { alpha, makeStyles } from '@material-ui/core/styles'
import Pagination from '@mui/material/Pagination'
import startCase from 'lodash/startCase'
import HttpHandler from 'app/reusable/HttpHandler'
import { TOAST_FEEDBACK } from 'app/reusable/utils'
import { useDismissModal, useModal } from 'app/ducks/modal/hooks'
import { useHasPermission, useIsUser } from 'app/ducks/auth/hooks'
import { USER_TYPE } from 'app/ducks/auth/types'
import useIsMobile from 'app/hooks/useIsMobile'
import { Loading } from '../SimpleList/SimpleList'
import ActionsMenu from './ActionsMenu'
import FilterChip from './FilterChip'
import useModelList from './useModelList'
import { isFilterActive } from './listQuery'

const useStyles = makeStyles(({ palette }) => ({
  card: { padding: 0, overflow: 'hidden' },
  top: { padding: '16px 16px 12px' },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 12,
  },
  title: { margin: 0, fontSize: 20, fontWeight: 700 },
  headerActions: { display: 'flex', alignItems: 'center', gap: 4 },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  search: {
    flex: '0 1 360px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    backgroundColor: palette.action.hover,
    '& .material-icons': { fontSize: 20, color: palette.text.secondary },
  },
  searchMobile: { flex: 1 },
  count: {
    marginLeft: 'auto',
    fontSize: 13,
    fontWeight: 600,
    color: palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  chipsMobile: {
    flexWrap: 'nowrap',
    overflowX: 'auto',
    margin: '0 -16px',
    padding: '2px 16px',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  textButton: {
    flexShrink: 0,
    fontSize: 13,
    fontWeight: 600,
    padding: '6px 8px',
    borderRadius: 6,
    color: palette.primary.main,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: palette.action.hover },
  },
  mobileCount: { marginTop: 10, marginLeft: 0 },
  progress: { height: 2 },
  progressPlaceholder: { height: 2 },
  body: { transition: 'opacity 150ms ease-in' },
  bodyLoading: { opacity: 0.5, pointerEvents: 'none' },
  headCell: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: palette.text.secondary,
    padding: '10px 16px',
    whiteSpace: 'nowrap',
  },
  cell: { padding: '10px 16px' },
  row: { '&:hover': { backgroundColor: palette.action.hover } },
  mobileList: { listStyle: 'none', margin: 0, padding: 0 },
  mobileItem: { borderTop: `1px solid ${palette.divider}` },
  mobileSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 8px 10px 16px',
  },
  mobileToggle: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    outline: 'none',
    '&:focus-visible': { boxShadow: `0 0 0 2px ${palette.primary.main}` },
  },
  mobilePrimary: { flex: 1, minWidth: 0 },
  expandIcon: {
    color: palette.text.secondary,
    transition: 'transform 150ms ease-in',
  },
  expandIconOpen: { transform: 'rotate(180deg)' },
  details: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '6px 12px',
    margin: 0,
    padding: '0 16px 12px 16px',
    fontSize: 13,
    '& dt': { color: palette.text.secondary },
    '& dd': { margin: 0, minWidth: 0, overflowWrap: 'anywhere' },
  },
  empty: {
    padding: '40px 16px',
    textAlign: 'center',
    color: palette.text.secondary,
    '& .material-icons': { fontSize: 40, opacity: 0.5 },
    '& p': { margin: '8px 0 12px' },
  },
  selectionBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    color: palette.primary.main,
    backgroundColor: alpha(palette.primary.main, 0.08),
  },
  selectionCount: { marginRight: 'auto' },
  checkboxCell: { width: 48, padding: '0 0 0 8px' },
  mobileCheckbox: { marginLeft: -8 },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '12px 8px 16px',
    borderTop: `1px solid ${palette.divider}`,
  },
}))

const columnMobileRole = (column) =>
  column.mobile === undefined ? 'detail' : column.mobile

const formatCount = (count, { singular, plural }) =>
  `${count.toLocaleString('pt-BR')} ${count === 1 ? singular : plural}`

const MobileRow = ({ row, columns, actions, selection }) => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)

  const primary = columns.find((c) => columnMobileRole(c) === 'primary')
  const aside = columns.filter((c) => columnMobileRole(c) === 'aside')
  const details = columns.filter((c) => columnMobileRole(c) === 'detail')

  const toggle = () => setExpanded((prev) => !prev)

  return (
    <li className={clsx('t-list-row', classes.mobileItem)}>
      <div className={classes.mobileSummary}>
        {selection && (
          <Checkbox
            size="small"
            color="primary"
            className={classes.mobileCheckbox}
            checked={selection.checked}
            disabled={!selection.enabled}
            onChange={selection.toggle}
            inputProps={{ 'aria-label': 'Selecionar' }}
          />
        )}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          className={classes.mobileToggle}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              toggle()
            }
          }}
        >
          <div className={classes.mobilePrimary}>
            {primary && (primary.renderMobile || primary.render)(row)}
          </div>
          {aside.map((column) => (
            <div key={column.key}>
              {(column.renderMobile || column.render)(row)}
            </div>
          ))}
          {!!details.length && (
            <Icon
              aria-hidden
              className={clsx(
                classes.expandIcon,
                expanded && classes.expandIconOpen
              )}
            >
              expand_more
            </Icon>
          )}
        </div>
        <ActionsMenu actions={actions} />
      </div>
      {!!details.length && (
        <Collapse in={expanded} unmountOnExit>
          <dl className={classes.details}>
            {details.map((column) => (
              <React.Fragment key={column.key}>
                <dt>{column.header}</dt>
                <dd>{(column.renderMobile || column.render)(row) ?? '—'}</dd>
              </React.Fragment>
            ))}
          </dl>
        </Collapse>
      )}
    </li>
  )
}

/**
 * Reusable list page over the backend's generic list API
 * (`/<app>/api/list/<model>`): search, filter chips (include/exclude),
 * total count, pagination and per-row "⋮" actions, with a compact,
 * expandable row layout on mobile. Search, filters and page live in the
 * URL. Optional row selection with bulk actions (`selection`), a summary
 * slot above the list that sees the applied filters (`summary`), and extra
 * header content (`headerContent`). See `docs/MODEL_LIST_PAGE.md` for the
 * full config.
 *
 * `ref.current.reload()` refetches the current page.
 */
const ModelListPage = forwardRef(
  (
    {
      djangoApp,
      djangoModel,
      title: customTitle,
      countLabel = { singular: 'registro', plural: 'registros' },
      columns,
      filters = [],
      defaultFilters = {},
      orderBy,
      pageSize = 20,
      rowKey = 'id',
      rowActions,
      headerActions = [],
      showAdd = true,
      onAdd,
      addLabel = 'Adicionar',
      // Pages whose route already restricts who gets in (and whose old list
      // showed the button to everyone there) can skip the `add` check.
      checkAddPermission = true,
      showEdit = true,
      showDelete = true,
      deleteConfirmText = 'Tem certeza que deseja excluir este registro?',
      searchPlaceholder = 'Buscar…',
      emptyStateText = 'Nenhum registro encontrado',
      baseParams,
      headerContent,
      summary,
      selection,
    },
    ref
  ) => {
    const classes = useStyles()
    const history = useHistory()
    const isMobile = useIsMobile()
    const showModal = useModal()
    const dismissModal = useDismissModal()

    const isStaff = useIsUser([
      USER_TYPE.ADMINISTRATOR,
      USER_TYPE.MANAGER,
      USER_TYPE.COLLABORATOR,
    ])
    const hasAddPermission = useHasPermission(djangoModel, 'add') && isStaff
    const canAdd = !checkAddPermission || hasAddPermission
    const canChange = useHasPermission(djangoModel, 'change') && isStaff
    const canDelete = useHasPermission(djangoModel, 'delete') && isStaff

    // Filters marked `secondary` stay behind "Mais filtros" until shown or
    // applied, so a page can offer one per field without a wall of chips.
    const [showSecondaryFilters, setShowSecondaryFilters] = useState(false)

    const list = useModelList({
      djangoApp,
      djangoModel,
      filters,
      defaultFilters,
      orderBy,
      pageSize,
      baseParams,
    })

    // Selected rows by key, kept across pages; reset when the filters or
    // search change, since the selection may no longer match them.
    const [selected, setSelected] = useState({})
    useEffect(() => setSelected({}), [list.resultsKey])

    const isSelectable = (row) =>
      !selection?.isSelectable || selection.isSelectable(row)
    const selectedRows = Object.values(selected)
    const selectableOnPage = list.rows.filter(isSelectable)
    const pageSelectedCount = selectableOnPage.filter(
      (row) => selected[row[rowKey]]
    ).length

    const toggleRow = (row) =>
      setSelected((prev) => {
        const next = { ...prev }
        if (next[row[rowKey]]) delete next[row[rowKey]]
        else next[row[rowKey]] = row
        return next
      })

    const togglePage = () =>
      setSelected((prev) => {
        const next = { ...prev }
        const selectAll = pageSelectedCount < selectableOnPage.length
        selectableOnPage.forEach((row) => {
          if (selectAll) next[row[rowKey]] = row
          else delete next[row[rowKey]]
        })
        return next
      })

    const rowSelection = (row) =>
      selection && {
        checked: !!selected[row[rowKey]],
        enabled: isSelectable(row),
        toggle: () => toggleRow(row),
      }

    useImperativeHandle(
      ref,
      () => ({
        reload: list.reload,
        clearSelection: () => setSelected({}),
      }),
      [list.reload]
    )

    const hiddenSecondaryCount = showSecondaryFilters
      ? 0
      : filters.filter(
          (definition) =>
            definition.secondary &&
            !isFilterActive(definition, list.filters[definition.key])
        ).length

    const formPath = `/${djangoModel}/form`

    const confirmDelete = useCallback(
      (row) =>
        showModal({
          title: 'Exclusão',
          onConfirm: () =>
            toast
              .promise(
                HttpHandler.delete(djangoApp, djangoModel, row[rowKey]),
                TOAST_FEEDBACK.DELETE
              )
              .then(() => {
                dismissModal()
                list.reload()
              }),
          children: deleteConfirmText,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [djangoApp, djangoModel, rowKey, deleteConfirmText, list.reload]
    )

    const actionsFor = (row) =>
      [
        ...(rowActions ? rowActions(row) : []),
        showEdit &&
          canChange && {
            key: 'edit',
            label: 'Editar',
            icon: 'edit',
            onClick: () => history.push(`${formPath}?id=${row[rowKey]}`),
          },
        showDelete &&
          canDelete && {
            key: 'delete',
            label: 'Excluir',
            icon: 'delete',
            danger: true,
            onClick: () => confirmDelete(row),
          },
      ].filter(Boolean)

    // `headerActions` may be a function of the list, for actions that depend
    // on the applied filters/search.
    const visibleHeaderActions = (
      typeof headerActions === 'function' ? headerActions(list) : headerActions
    ).filter(Boolean)
    const title = customTitle || startCase(list.title?.toLowerCase())
    const hasCount = typeof list.count === 'number'

    const countElement = hasCount && (
      <span
        className={clsx(
          't-list-count',
          classes.count,
          isMobile && classes.mobileCount
        )}
        aria-live="polite"
      >
        {formatCount(list.count, countLabel)}
      </span>
    )

    const addButton =
      showAdd &&
      canAdd &&
      (isMobile ? (
        <IconButton
          color="primary"
          aria-label={addLabel}
          onClick={onAdd || (() => history.push(formPath))}
        >
          <Icon>add</Icon>
        </IconButton>
      ) : (
        <Button
          color="primary"
          variant="contained"
          size="small"
          disableElevation
          startIcon={<Icon>add</Icon>}
          onClick={onAdd || (() => history.push(formPath))}
        >
          {addLabel}
        </Button>
      ))

    const renderEmpty = () => (
      <div className={classes.empty}>
        <Icon aria-hidden>search_off</Icon>
        <p>{emptyStateText}</p>
        {list.hasActiveFilters && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={list.clearFilters}
          >
            Limpar filtros
          </Button>
        )}
      </div>
    )

    const renderRows = () => {
      if (!list.rows.length) return renderEmpty()

      if (isMobile) {
        return (
          <ul className={classes.mobileList}>
            {list.rows.map((row) => (
              <MobileRow
                key={row[rowKey]}
                row={row}
                columns={columns}
                actions={actionsFor(row)}
                selection={rowSelection(row)}
              />
            ))}
          </ul>
        )
      }

      return (
        <Table>
          <TableHead>
            <TableRow>
              {selection && (
                <TableCell className={classes.checkboxCell}>
                  <Checkbox
                    size="small"
                    color="primary"
                    disabled={!selectableOnPage.length}
                    checked={
                      !!selectableOnPage.length &&
                      pageSelectedCount === selectableOnPage.length
                    }
                    indeterminate={
                      pageSelectedCount > 0 &&
                      pageSelectedCount < selectableOnPage.length
                    }
                    onChange={togglePage}
                    inputProps={{ 'aria-label': 'Selecionar página' }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={classes.headCell}
                  style={{ width: column.width }}
                  align={column.align}
                >
                  {column.header}
                </TableCell>
              ))}
              <TableCell
                className={classes.headCell}
                style={{ width: 56 }}
                aria-label="Ações"
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.rows.map((row) => (
              <TableRow
                key={row[rowKey]}
                className={clsx('t-list-row', classes.row)}
                selected={!!selected[row[rowKey]]}
              >
                {selection && (
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={!!selected[row[rowKey]]}
                      disabled={!isSelectable(row)}
                      onChange={() => toggleRow(row)}
                      inputProps={{ 'aria-label': 'Selecionar' }}
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={classes.cell}
                    align={column.align}
                  >
                    {column.render(row)}
                  </TableCell>
                ))}
                <TableCell className={classes.cell} align="right">
                  <ActionsMenu actions={actionsFor(row)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )
    }

    return (
      <div className="m-sm-30 mt-6">
        {summary && summary(list)}
        <Card className={classes.card}>
          <div className={classes.top}>
            <div className={classes.titleRow}>
              <h1 className={classes.title}>{title}</h1>
              <div className={classes.headerActions}>
                {headerContent}
                {isMobile ? (
                  <ActionsMenu
                    actions={visibleHeaderActions}
                    label="Mais opções"
                  />
                ) : (
                  visibleHeaderActions.map((action) => {
                    const button = (
                      <IconButton
                        size="small"
                        className="p-2"
                        aria-label={action.label}
                        onClick={action.onClick}
                      >
                        <Icon fontSize="small">{action.icon}</Icon>
                      </IconButton>
                    )
                    return (
                      <Tooltip key={action.key} title={action.label}>
                        {action.to ? (
                          <Link to={action.to}>{button}</Link>
                        ) : (
                          button
                        )}
                      </Tooltip>
                    )
                  })
                )}
                {addButton}
              </div>
            </div>

            <div className={classes.searchRow}>
              <label
                className={clsx(
                  classes.search,
                  isMobile && classes.searchMobile
                )}
              >
                <Icon aria-hidden>search</Icon>
                <InputBase
                  fullWidth
                  type="search"
                  value={list.searchText}
                  placeholder={searchPlaceholder}
                  inputProps={{ 'aria-label': searchPlaceholder }}
                  onChange={(e) => list.setSearchText(e.target.value)}
                />
              </label>
              {!isMobile && countElement}
            </div>

            {!!filters.length && (
              <div
                className={clsx(classes.chips, isMobile && classes.chipsMobile)}
              >
                {filters
                  .filter(
                    (definition) =>
                      !definition.secondary ||
                      showSecondaryFilters ||
                      isFilterActive(definition, list.filters[definition.key])
                  )
                  .map((definition) => (
                    <FilterChip
                      key={definition.key}
                      definition={definition}
                      value={list.filters[definition.key]}
                      onChange={(value) =>
                        list.setFilter(definition.key, value)
                      }
                    />
                  ))}
                {hiddenSecondaryCount > 0 && (
                  <ButtonBase
                    className={classes.textButton}
                    onClick={() => setShowSecondaryFilters(true)}
                  >
                    {`+ Mais filtros (${hiddenSecondaryCount})`}
                  </ButtonBase>
                )}
                {showSecondaryFilters && (
                  <ButtonBase
                    className={classes.textButton}
                    onClick={() => setShowSecondaryFilters(false)}
                  >
                    Menos filtros
                  </ButtonBase>
                )}
                {list.hasActiveFilters && (
                  <ButtonBase
                    className={classes.textButton}
                    onClick={list.clearFilters}
                  >
                    Limpar filtros
                  </ButtonBase>
                )}
                {list.hasDefaultFilters && !list.isDefaultFilters && (
                  <ButtonBase
                    className={classes.textButton}
                    onClick={list.restoreDefaults}
                  >
                    Restaurar padrão
                  </ButtonBase>
                )}
              </div>
            )}
            {isMobile && countElement}
          </div>

          {selection && selectedRows.length > 0 && (
            <div className={clsx('t-selection-bar', classes.selectionBar)}>
              <span className={classes.selectionCount} aria-live="polite">
                {selectedRows.length === 1
                  ? '1 selecionado'
                  : `${selectedRows.length} selecionados`}
              </span>
              {selection.actions.map((action) => (
                <Button
                  key={action.key}
                  size="small"
                  color="primary"
                  variant="contained"
                  disableElevation
                  startIcon={action.icon && <Icon>{action.icon}</Icon>}
                  onClick={() => action.onClick(selectedRows)}
                >
                  {action.label}
                </Button>
              ))}
              <ButtonBase
                className={classes.textButton}
                onClick={() => setSelected({})}
              >
                Limpar seleção
              </ButtonBase>
            </div>
          )}

          {list.isLoading && !list.isFirstLoad ? (
            <LinearProgress className={classes.progress} />
          ) : (
            <div className={classes.progressPlaceholder} />
          )}

          {list.isFirstLoad ? (
            <Loading />
          ) : (
            <div
              className={clsx(
                classes.body,
                list.isLoading && classes.bodyLoading
              )}
              aria-busy={list.isLoading}
            >
              {renderRows()}
            </div>
          )}

          {list.pageCount > 1 && (
            <div className={classes.footer}>
              <Pagination
                page={list.page}
                count={list.pageCount}
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={1}
                onChange={(_, page) => list.setPage(page)}
              />
            </div>
          )}
        </Card>
      </div>
    )
  }
)

export default ModelListPage
